import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import tradeShowService from '../../services/tradeShowService';
import customerService from '../../services/customerService';
import SearchableSelect from '../../components/SearchableSelect';
import ConfirmModal from '../../components/ConfirmModal';
import TrendBarChart from '../../components/charts/TrendBarChart';

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const EXPENSE_CATEGORIES = [
  ['booth_fee', 'Booth Fee'], ['travel', 'Travel'], ['lodging', 'Lodging'],
  ['materials', 'Materials'], ['staff_time', 'Staff Time'], ['shipping', 'Shipping'],
  ['marketing', 'Marketing'], ['other', 'Other'],
];
const LEAD_STATUSES = [
  ['new', 'New'], ['contacted', 'Contacted'], ['sampled', 'Sampled'],
  ['converted', 'Converted'], ['not_interested', 'Not Interested'],
];
const BUSINESS_TYPES = [['retail', 'Retail'], ['food_service', 'Food Service'], ['distributor', 'Distributor'], ['other', 'Other']];

// CSV import field labels — mirrors services/leadImportAiService.js's TARGET_FIELDS keys.
const IMPORT_FIELD_LABELS = {
  fullName: 'Name',
  businessName: 'Business',
  email: 'Email',
  phone: 'Phone',
  title: 'Title',
  notes: 'Notes',
  brokerName: 'Broker',
  brokerEmail: 'Broker Email',
  vendorName: 'Vendor',
  vendorEmail: 'Vendor Email',
  accountNumber: 'Account #',
  scName: 'Sales Consultant',
  scPhone: 'SC Phone',
  scEmail: 'SC Email',
  dsmName: 'DSM',
};
const IMPORT_FIELD_ORDER = Object.keys(IMPORT_FIELD_LABELS);

// ── KPI card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, accent }) {
  return (
    <div className="col-6 col-md-4 col-lg-2">
      <div className="card h-100">
        <div className="card-body text-center py-3">
          <div className="text-muted text-uppercase" style={{ fontSize: 11, letterSpacing: 0.5 }}>{label}</div>
          <div className="fw-bold" style={{ fontSize: 22, color: accent || '#1b2638' }}>{value}</div>
          {sub && <div className="text-muted" style={{ fontSize: 11 }}>{sub}</div>}
        </div>
      </div>
    </div>
  );
}

// ── Overview tab ─────────────────────────────────────────────────────────────
function OverviewTab({ pnl }) {
  if (!pnl) return <div className="text-center py-5"><span className="spinner-border" /></div>;
  const roiColor = pnl.roiPct == null ? undefined : pnl.roiPct >= 0 ? '#198754' : '#dc3545';
  const channelLabels = ['Retail', 'Food Service', 'Unspecified'];
  const channelValues = [pnl.revenueByChannel.retail, pnl.revenueByChannel.food_service, pnl.revenueByChannel.unspecified];

  return (
    <div>
      <div className="row g-3 mb-4">
        <StatCard label="Leads Captured" value={pnl.leadsCaptured} />
        <StatCard label="Converted" value={pnl.leadsConverted} sub={pnl.conversionRatePct != null ? `${pnl.conversionRatePct.toFixed(0)}% conversion` : null} />
        <StatCard label="Total Cost" value={money(pnl.totalCost)} />
        <StatCard label="Revenue" value={money(pnl.revenueGenerated)} accent="#198754" />
        <StatCard label="ROI" value={pnl.roiPct == null ? '—' : `${pnl.roiPct >= 0 ? '+' : ''}${pnl.roiPct.toFixed(0)}%`} accent={roiColor} />
        <StatCard label="Cost / Lead" value={pnl.costPerLead == null ? '—' : money(pnl.costPerLead)} />
      </div>
      <div className="card">
        <div className="card-header" style={{ backgroundColor: '#1b2638', color: 'white' }}>
          Revenue by Business Type
        </div>
        <div className="card-body">
          {channelValues.every(v => !v) ? (
            <p className="text-muted mb-0">No converted-lead revenue yet.</p>
          ) : (
            <TrendBarChart labels={channelLabels} values={channelValues} yAxisTitle="Revenue ($)" colors={['#0d6efd', '#fd7e14', '#adb5bd']} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Expenses tab ─────────────────────────────────────────────────────────────
function ExpensesTab({ showId, onChanged }) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ category: 'booth_fee', description: '', amount: '', date: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    tradeShowService.listExpenses(showId).then(setExpenses).catch(() => {}).finally(() => setLoading(false));
  }, [showId]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleAdd = async () => {
    if (!form.amount || !form.date) { setError('Amount and date are required.'); return; }
    setSaving(true); setError('');
    try {
      await tradeShowService.addExpense(showId, { ...form, amount: parseFloat(form.amount) });
      setForm({ category: 'booth_fee', description: '', amount: '', date: '', notes: '' });
      load();
      onChanged();
    } catch {
      setError('Failed to add expense.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    await tradeShowService.deleteExpense(deleteTarget.id);
    setDeleteTarget(null);
    load();
    onChanged();
  };

  const total = expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);

  return (
    <div>
      <div className="border rounded p-3 bg-light mb-3">
        <h6 className="small fw-semibold text-muted mb-2">Add Expense</h6>
        <div className="row g-2 align-items-end">
          <div className="col-md-2">
            <label className="form-label small mb-1">Category</label>
            <select className="form-select form-select-sm" value={form.category} onChange={e => set('category', e.target.value)}>
              {EXPENSE_CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label small mb-1">Description</label>
            <input className="form-control form-control-sm" value={form.description} onChange={e => set('description', e.target.value)} placeholder="e.g. 10x10 booth rental" />
          </div>
          <div className="col-md-2">
            <label className="form-label small mb-1">Amount ($)</label>
            <input type="number" min="0" step="0.01" className="form-control form-control-sm" value={form.amount} onChange={e => set('amount', e.target.value)} />
          </div>
          <div className="col-md-2">
            <label className="form-label small mb-1">Date</label>
            <input type="date" className="form-control form-control-sm" value={form.date} onChange={e => set('date', e.target.value)} />
          </div>
          <div className="col-md-3">
            <button className="btn btn-primary btn-sm w-100" onClick={handleAdd} disabled={saving}>
              {saving ? 'Saving…' : '+ Add Expense'}
            </button>
          </div>
        </div>
        {error && <div className="text-danger small mt-2">{error}</div>}
      </div>

      {loading ? (
        <div className="text-center py-4"><span className="spinner-border spinner-border-sm" /></div>
      ) : expenses.length === 0 ? (
        <p className="text-muted fst-italic">No expenses logged yet.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-bordered align-middle">
            <thead className="table-light">
              <tr><th>Date</th><th>Category</th><th>Description</th><th className="text-end">Amount</th><th /></tr>
            </thead>
            <tbody>
              {expenses.map(e => (
                <tr key={e.id}>
                  <td className="small">{e.date}</td>
                  <td className="small">{EXPENSE_CATEGORIES.find(c => c[0] === e.category)?.[1] || e.category}</td>
                  <td className="small">{e.description || <span className="text-muted">—</span>}</td>
                  <td className="text-end">{money(e.amount)}</td>
                  <td><button className="btn btn-outline-danger btn-sm px-2" onClick={() => setDeleteTarget(e)}>🗑</button></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="table-light fw-semibold">
                <td colSpan={3} className="text-end">Total</td>
                <td className="text-end">{money(total)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <ConfirmModal
        show={!!deleteTarget}
        title="Delete expense?"
        message={`Remove this ${deleteTarget?.category?.replace('_', ' ')} expense of ${money(deleteTarget?.amount)}?`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

// ── Convert-to-customer modal ──────────────────────────────────────────────────
function ConvertLeadModal({ lead, onClose, onConverted }) {
  const [customers, setCustomers] = useState([]);
  const [mode, setMode] = useState('new'); // 'new' | 'existing'
  const [customerId, setCustomerId] = useState('');
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (lead) {
      setNewName(lead.businessName || lead.fullName || '');
      setMode('new');
      setCustomerId('');
      setError('');
      customerService.getCustomers().then(setCustomers).catch(() => {});
    }
  }, [lead]);

  const handleConvert = async () => {
    setSaving(true); setError('');
    try {
      const payload = mode === 'existing' ? { customerId } : { name: newName.trim() };
      const result = await tradeShowService.convertLead(lead.id, payload);
      onConverted(result.lead);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to convert lead.');
    } finally {
      setSaving(false);
    }
  };

  if (!lead) return null;
  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} onClick={onClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1070 }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 460 }}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Convert Lead to Customer</h5>
              <button className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
              <div className="btn-group w-100 mb-3">
                <button className={`btn btn-sm ${mode === 'new' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setMode('new')}>Create New Customer</button>
                <button className={`btn btn-sm ${mode === 'existing' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setMode('existing')}>Link Existing Customer</button>
              </div>
              {mode === 'new' ? (
                <div>
                  <label className="form-label small mb-1">Customer Name</label>
                  <input className="form-control form-control-sm" value={newName} onChange={e => setNewName(e.target.value)} />
                </div>
              ) : (
                <div>
                  <label className="form-label small mb-1">Existing Customer</label>
                  <SearchableSelect
                    options={customers.map(c => ({ value: c.id, label: c.name }))}
                    value={customerId}
                    onChange={setCustomerId}
                    placeholder="Search customers…"
                    size="sm"
                  />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
              <button className="btn btn-success btn-sm" onClick={handleConvert} disabled={saving || (mode === 'existing' && !customerId) || (mode === 'new' && !newName.trim())}>
                {saving ? 'Converting…' : 'Convert'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Add lead modal (manual + AI business card scan) ────────────────────────────
function LeadFormModal({ show, showId, lead, onClose, onSaved }) {
  const blank = { fullName: '', email: '', phone: '', businessName: '', title: '', businessType: '', notes: '', status: 'new' };
  const [form, setForm] = useState(blank);
  const [mode, setMode] = useState('manual'); // 'manual' | 'scan'
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [businessCardImageUrl, setBusinessCardImageUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = React.useRef();

  useEffect(() => {
    if (show) {
      setForm(lead ? {
        fullName: lead.fullName || '', email: lead.email || '', phone: lead.phone || '',
        businessName: lead.businessName || '', title: lead.title || '', businessType: lead.businessType || '',
        notes: lead.notes || '', status: lead.status || 'new',
      } : blank);
      setMode('manual'); setBusinessCardImageUrl(null); setScanError(''); setError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, lead]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true); setScanError('');
    try {
      const imageBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { extracted, businessCardImageUrl: url } = await tradeShowService.scanCard(showId, imageBase64, file.type || 'image/jpeg');
      setForm(f => ({
        ...f,
        fullName: extracted.fullName || f.fullName,
        email: extracted.email || f.email,
        phone: extracted.phone || f.phone,
        businessName: extracted.businessName || f.businessName,
        title: extracted.title || f.title,
      }));
      setBusinessCardImageUrl(url);
    } catch (err) {
      setScanError(err?.response?.data?.error || 'Failed to scan card — enter details manually below.');
    } finally {
      setScanning(false);
    }
  };

  const handleSave = async () => {
    if (!form.fullName.trim()) { setError('Full name is required.'); return; }
    setSaving(true); setError('');
    try {
      const saved = lead
        ? await tradeShowService.updateLead(lead.id, form)
        : await tradeShowService.addLead(showId, {
            ...form,
            source: businessCardImageUrl ? 'business_card_scan' : 'manual',
            businessCardImageUrl,
          });
      onSaved(saved);
    } catch (err) {
      const message = err.code === 'ECONNABORTED'
        ? 'Request timed out — check your connection and try again.'
        : (err?.response?.data?.error || 'Failed to save lead.');
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;
  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} onClick={onClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1070 }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 480 }}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{lead ? 'Edit Lead' : 'Add Lead'}</h5>
              <button className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

              {!lead && (
                <div className="btn-group w-100 mb-3">
                  <button className={`btn btn-sm ${mode === 'manual' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setMode('manual')}>✏️ Manual Entry</button>
                  <button className={`btn btn-sm ${mode === 'scan' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setMode('scan')}>📇 Scan Business Card</button>
                </div>
              )}

              {!lead && mode === 'scan' && (
                <div className="mb-3 text-center">
                  {businessCardImageUrl && <img src={businessCardImageUrl} alt="card" style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 6, border: '1px solid #dee2e6' }} className="mb-2 d-block mx-auto" />}
                  <button className="btn btn-outline-primary w-100" style={{ padding: 14 }} onClick={() => fileRef.current.click()} disabled={scanning}>
                    {scanning ? <><span className="spinner-border spinner-border-sm me-2" />Reading card…</> : '📷 Take / Select Photo'}
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleFileChange} />
                  {scanError && <div className="text-danger small mt-2">{scanError}</div>}
                  {businessCardImageUrl && <div className="text-success small mt-2">✓ Extracted — review the fields below before saving</div>}
                </div>
              )}

              <div className="mb-2">
                <label className="form-label small mb-1">Full Name <span className="text-danger">*</span></label>
                <input className="form-control form-control-sm" value={form.fullName} onChange={e => set('fullName', e.target.value)} />
              </div>
              <div className="row g-2 mb-2">
                <div className="col-6">
                  <label className="form-label small mb-1">Email</label>
                  <input className="form-control form-control-sm" value={form.email} onChange={e => set('email', e.target.value)} />
                </div>
                <div className="col-6">
                  <label className="form-label small mb-1">Phone</label>
                  <input className="form-control form-control-sm" value={form.phone} onChange={e => set('phone', e.target.value)} />
                </div>
              </div>
              <div className="row g-2 mb-2">
                <div className="col-8">
                  <label className="form-label small mb-1">Business Name</label>
                  <input className="form-control form-control-sm" value={form.businessName} onChange={e => set('businessName', e.target.value)} />
                </div>
                <div className="col-4">
                  <label className="form-label small mb-1">Title</label>
                  <input className="form-control form-control-sm" value={form.title} onChange={e => set('title', e.target.value)} />
                </div>
              </div>
              <div className="mb-2">
                <label className="form-label small mb-1">Type of Business</label>
                <select className="form-select form-select-sm" value={form.businessType} onChange={e => set('businessType', e.target.value)}>
                  <option value="">— not specified —</option>
                  {BUSINESS_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="mb-2">
                <label className="form-label small mb-1">Notes</label>
                <textarea className="form-control form-control-sm" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
              </div>
              {lead && (
                <div className="mb-2">
                  <label className="form-label small mb-1">Status</label>
                  <select className="form-select form-select-sm" value={form.status} onChange={e => set('status', e.target.value)} disabled={!!lead.customerId}>
                    {LEAD_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  {lead.customerId && (
                    <div className="form-text" style={{ fontSize: 11 }}>
                      Already converted to a customer — status is locked.
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : lead ? 'Save Changes' : 'Save Lead'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Leads tab ────────────────────────────────────────────────────────────────
// ── CSV import — column mapping review modal ─────────────────────────────────
function ImportLeadsMappingModal({ state, onFieldChange, onCancel, onConfirm, importing }) {
  if (!state) return null;
  const { headers, mapping, confidence, notes, totalRows, sampleRows } = state;
  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} onClick={onCancel} />
      <div className="modal fade show d-block" style={{ zIndex: 1070 }} tabIndex={-1}>
        <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Review Column Mapping</h5>
              <button className="btn-close" onClick={onCancel} />
            </div>
            <div className="modal-body">
              <p className="text-muted small">
                {totalRows} row{totalRows === 1 ? '' : 's'} found. AI matched your CSV's columns to
                our fields below (confidence: {confidence}) — review and correct anything before importing.
              </p>
              {notes && <div className="alert alert-warning py-2 small">{notes}</div>}
              <table className="table table-sm">
                <thead><tr><th>Our Field</th><th>CSV Column</th></tr></thead>
                <tbody>
                  {IMPORT_FIELD_ORDER.map(field => (
                    <tr key={field}>
                      <td className="small fw-semibold">
                        {IMPORT_FIELD_LABELS[field]}{field === 'fullName' && <span className="text-danger"> *</span>}
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={mapping[field] || ''}
                          onChange={e => onFieldChange(field, e.target.value)}
                        >
                          <option value="">— none —</option>
                          {headers.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {sampleRows?.length > 0 && (
                <div className="mt-3">
                  <div className="text-muted small mb-1">Sample rows from your file:</div>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead><tr>{headers.map(h => <th key={h} className="small">{h}</th>)}</tr></thead>
                      <tbody>
                        {sampleRows.map((row, i) => (
                          <tr key={i}>{headers.map(h => <td key={h} className="small">{row[h]}</td>)}</tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={onCancel} disabled={importing}>Cancel</button>
              <button className="btn btn-primary" onClick={onConfirm} disabled={importing || !mapping.fullName}>
                {importing ? 'Importing…' : `Import ${totalRows} Row${totalRows === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function LeadsTab({ showId, onChanged }) {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [addModal, setAddModal] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [convertLead, setConvertLead] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expanded, setExpanded] = useState({}); // { [leadId]: true }
  const toggleExpand = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));
  const [importState, setImportState] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef();

  const load = useCallback(() => {
    setLoading(true);
    tradeShowService.listLeads(showId).then(setLeads).catch(() => {}).finally(() => setLoading(false));
  }, [showId]);

  useEffect(() => { load(); }, [load]);

  const handleAdded = () => { setAddModal(false); load(); onChanged(); };
  const handleEdited = () => { setEditLead(null); load(); onChanged(); };
  const handleConverted = () => { setConvertLead(null); load(); onChanged(); };
  const handleDelete = async () => { await tradeShowService.deleteLead(deleteTarget.id); setDeleteTarget(null); load(); onChanged(); };

  const handleExport = async () => {
    const blob = await tradeShowService.exportLeads(showId);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `trade-show-${showId}-leads.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportError('');
    setImportResult(null);
    try {
      const csvText = await file.text();
      const preview = await tradeShowService.previewLeadImport(showId, csvText);
      setImportState({ csvText, ...preview });
    } catch (err) {
      setImportError(err?.response?.data?.error || 'Failed to read/preview this CSV.');
    }
  };

  const setImportMappingField = (field, header) => {
    setImportState(s => ({ ...s, mapping: { ...s.mapping, [field]: header || null } }));
  };

  const handleConfirmImport = async () => {
    setImporting(true);
    setImportError('');
    try {
      const result = await tradeShowService.confirmLeadImport(showId, importState.csvText, importState.mapping);
      setImportResult(result);
      setImportState(null);
      load();
      onChanged();
    } catch (err) {
      setImportError(err?.response?.data?.error || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  const q = searchQuery.trim().toLowerCase();
  const filtered = leads
    .filter(l => !statusFilter || l.status === statusFilter)
    .filter(l => !q || [l.fullName, l.email, l.phone, l.businessName, l.title].some(v => v && v.toLowerCase().includes(q)));
  const SOURCE_ICON = { manual: '✏️', business_card_scan: '📇', self_serve_qr: '📱', csv_import: '📥' };

  return (
    <div>
      {importError && <div className="alert alert-danger py-2">{importError}</div>}
      {importResult && (
        <div className="alert alert-success py-2 d-flex justify-content-between align-items-center">
          <span>
            Imported {importResult.created} lead{importResult.created === 1 ? '' : 's'}
            {importResult.skipped ? ` (${importResult.skipped} row${importResult.skipped === 1 ? '' : 's'} skipped — missing a name)` : ''}.
          </span>
          <button className="btn-close" onClick={() => setImportResult(null)} />
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
        <div className="d-flex gap-2">
          <input
            className="form-control form-control-sm" style={{ width: 220 }}
            placeholder="🔍 Search name, email, phone…"
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          />
          <select className="form-select form-select-sm" style={{ width: 180 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses ({leads.length})</option>
            {LEAD_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => fileRef.current.click()}>📥 Import CSV</button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={handleImportFile} />
          <button className="btn btn-outline-secondary btn-sm" onClick={handleExport} disabled={leads.length === 0}>⬇ Export CSV</button>
          <button className="btn btn-primary btn-sm" onClick={() => setAddModal(true)}>+ Add Lead</button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-4"><span className="spinner-border spinner-border-sm" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-muted fst-italic">No leads {q ? 'match your search' : statusFilter ? 'with this status' : 'captured yet'}.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-top">
            <thead className="table-light">
              <tr><th></th><th></th><th>Name</th><th>Business</th><th>Contact</th><th>Status</th><th>Customer</th><th className="text-end">Revenue</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.map(lead => {
                const isOpen = !!expanded[lead.id];
                const hasDetail = lead.title || lead.businessType || lead.notes || lead.businessCardImageUrl;
                return (
                  <React.Fragment key={lead.id}>
                    <tr>
                      <td>
                        {hasDetail && (
                          <button className="btn btn-link btn-sm p-0" onClick={() => toggleExpand(lead.id)} title={isOpen ? 'Collapse' : 'Show more'}>
                            {isOpen ? '▾' : '▸'}
                          </button>
                        )}
                      </td>
                      <td title={lead.source}>{SOURCE_ICON[lead.source] || ''}</td>
                      <td className="fw-semibold small">{lead.fullName}</td>
                      <td className="small">
                        {lead.businessName || <span className="text-muted">—</span>}
                        {lead.businessType && (
                          <div className="text-muted" style={{ fontSize: 11 }}>
                            {BUSINESS_TYPES.find(t => t[0] === lead.businessType)?.[1] || lead.businessType}
                          </div>
                        )}
                      </td>
                      <td className="small">{lead.email || lead.phone || <span className="text-muted">—</span>}</td>
                      <td>
                        <span className={`badge ${lead.status === 'converted' ? 'bg-success' : lead.status === 'not_interested' ? 'bg-secondary' : 'bg-info text-dark'}`}>
                          {LEAD_STATUSES.find(s => s[0] === lead.status)?.[1] || lead.status}
                        </span>
                      </td>
                      <td className="small">
                        {lead.Customer ? (
                          <button className="btn btn-link btn-sm p-0" onClick={() => navigate('/admin/customers')} title="Open Customers">
                            {lead.Customer.name}
                          </button>
                        ) : <span className="text-muted">—</span>}
                      </td>
                      <td className="text-end small">
                        {lead.customerId ? money(lead.revenueGenerated) : <span className="text-muted">—</span>}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button className="btn btn-outline-secondary btn-sm px-2" onClick={() => setEditLead(lead)} title="Edit">✏️</button>
                          {!lead.customerId && (
                            <button className="btn btn-outline-success btn-sm px-2" onClick={() => setConvertLead(lead)} title="Convert to customer">💲</button>
                          )}
                          <button className="btn btn-outline-danger btn-sm px-2" onClick={() => setDeleteTarget(lead)} title="Delete">🗑</button>
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td></td>
                        <td colSpan={7} className="bg-light small py-2">
                          <div className="row g-3">
                            {lead.title && (
                              <div className="col-md-3">
                                <div className="text-muted" style={{ fontSize: 11 }}>Title</div>
                                <div>{lead.title}</div>
                              </div>
                            )}
                            {lead.businessType && (
                              <div className="col-md-3">
                                <div className="text-muted" style={{ fontSize: 11 }}>Business Type</div>
                                <div>{BUSINESS_TYPES.find(t => t[0] === lead.businessType)?.[1] || lead.businessType}</div>
                              </div>
                            )}
                            {lead.businessCardImageUrl && (
                              <div className="col-md-3">
                                <div className="text-muted" style={{ fontSize: 11 }}>Business Card</div>
                                <a href={lead.businessCardImageUrl} target="_blank" rel="noreferrer">View image</a>
                              </div>
                            )}
                            {lead.notes && (
                              <div className="col-12">
                                <div className="text-muted" style={{ fontSize: 11 }}>Notes</div>
                                <div style={{ whiteSpace: 'pre-wrap' }}>{lead.notes}</div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <LeadFormModal show={addModal} showId={showId} onClose={() => setAddModal(false)} onSaved={handleAdded} />
      <LeadFormModal show={!!editLead} showId={showId} lead={editLead} onClose={() => setEditLead(null)} onSaved={handleEdited} />
      <ConvertLeadModal lead={convertLead} onClose={() => setConvertLead(null)} onConverted={handleConverted} />
      <ImportLeadsMappingModal
        state={importState}
        onFieldChange={setImportMappingField}
        onCancel={() => setImportState(null)}
        onConfirm={handleConfirmImport}
        importing={importing}
      />
      <ConfirmModal
        show={!!deleteTarget}
        title="Delete lead?"
        message={`Remove ${deleteTarget?.fullName} from this show's leads?`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

// ── Follow-up email tab ──────────────────────────────────────────────────────
function FollowUpTab({ show, onShowUpdated }) {
  const [highlights, setHighlights] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const [subject, setSubject] = useState(show.followUpEmailSubject || '');
  const [htmlBody, setHtmlBody] = useState(show.followUpEmailHtml || '');
  const [textBody, setTextBody] = useState(show.followUpEmailText || '');
  const [saving, setSaving] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true); setError('');
    try {
      const updated = await tradeShowService.generateFollowUpEmail(show.id, highlights);
      setSubject(updated.followUpEmailSubject || '');
      setHtmlBody(updated.followUpEmailHtml || '');
      setTextBody(updated.followUpEmailText || '');
      onShowUpdated(updated);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to generate email.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await tradeShowService.saveFollowUpEmail(show.id, { followUpEmailSubject: subject, followUpEmailHtml: htmlBody, followUpEmailText: textBody });
      onShowUpdated(updated);
    } finally {
      setSaving(false);
    }
  };

  const copy = async (text, label) => {
    await navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <div>
      <div className="border rounded p-3 bg-light mb-3">
        <label className="form-label small fw-semibold mb-1">Anything specific to mention? (optional)</label>
        <div className="d-flex gap-2">
          <input className="form-control form-control-sm" value={highlights} onChange={e => setHighlights(e.target.value)} placeholder="e.g. our new gluten-free line, a seasonal promo…" />
          <button className="btn btn-primary btn-sm text-nowrap" onClick={handleGenerate} disabled={generating}>
            {generating ? 'Writing…' : (subject ? '✨ Regenerate' : '✨ Generate with AI')}
          </button>
        </div>
        {error && <div className="text-danger small mt-2">{error}</div>}
      </div>

      {subject && (
        <div className="card">
          <div className="card-body">
            <label className="form-label small fw-semibold mb-1">Subject</label>
            <input className="form-control form-control-sm mb-3" value={subject} onChange={e => setSubject(e.target.value)} />

            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="form-label small fw-semibold mb-0">Email Body</label>
              <div className="d-flex gap-2">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => copy(htmlBody, 'html')}>{copied === 'html' ? '✓ Copied' : 'Copy HTML'}</button>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => copy(textBody, 'text')}>{copied === 'text' ? '✓ Copied' : 'Copy Plain Text'}</button>
              </div>
            </div>
            <textarea className="form-control form-control-sm mb-2" rows={10} value={htmlBody} onChange={e => setHtmlBody(e.target.value)} style={{ fontFamily: 'monospace', fontSize: 12 }} />

            <div className="border rounded p-3 bg-light mb-3">
              <div className="text-muted small mb-2">Preview</div>
              <div dangerouslySetInnerHTML={{ __html: htmlBody }} />
            </div>

            <button className="btn btn-outline-primary btn-sm" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Edits'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── QR / self-serve tab ──────────────────────────────────────────────────────
function QrTab({ show, leads }) {
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    tradeShowService.getQrCode(show.id).then(setQr).catch(() => {}).finally(() => setLoading(false));
  }, [show.id]);

  useEffect(() => { load(); }, [load]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    await tradeShowService.regenerateToken(show.id);
    await load();
    setRegenerating(false);
  };

  const selfServeCount = (leads || []).filter(l => l.source === 'self_serve_qr').length;

  if (loading) return <div className="text-center py-5"><span className="spinner-border" /></div>;
  return (
    <div className="row g-4">
      <div className="col-md-5 text-center">
        <div className="card">
          <div className="card-body">
            {qr && <img src={qr.qrDataUrl} alt="QR code" style={{ width: '100%', maxWidth: 260 }} />}
            <p className="text-muted small mt-2 mb-0">Print this and put it at your booth</p>
          </div>
        </div>
      </div>
      <div className="col-md-7">
        <label className="form-label small fw-semibold mb-1">Public link</label>
        <div className="input-group input-group-sm mb-3">
          <input className="form-control" readOnly value={qr?.captureUrl || ''} />
          <button className="btn btn-outline-secondary" onClick={() => { navigator.clipboard.writeText(qr.captureUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
            {copied ? '✓' : 'Copy'}
          </button>
        </div>
        <div className="alert alert-light border small">
          <strong>{selfServeCount}</strong> lead{selfServeCount === 1 ? '' : 's'} captured via this QR code so far.
        </div>
        <button className="btn btn-outline-danger btn-sm" onClick={handleRegenerate} disabled={regenerating}>
          {regenerating ? 'Regenerating…' : 'Regenerate Link (invalidates the old QR code)'}
        </button>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
const TABS = [
  ['overview', 'Overview'], ['leads', 'Leads'], ['expenses', 'Expenses'],
  ['followup', 'Follow-up Email'], ['qr', 'QR / Self-Serve'],
];

export default function TradeShowDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [show, setShow] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [leads, setLeads] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  const refreshPnl = useCallback(() => {
    tradeShowService.getPnl(id).then(setPnl).catch(() => {});
    tradeShowService.listLeads(id).then(setLeads).catch(() => {});
  }, [id]);

  useEffect(() => {
    setLoading(true);
    Promise.all([tradeShowService.getTradeShow(id), tradeShowService.getPnl(id), tradeShowService.listLeads(id)])
      .then(([s, p, l]) => { setShow(s); setPnl(p); setLeads(l); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading || !show) {
    return <div className="text-center py-5"><span className="spinner-border" /></div>;
  }

  return (
    <div className="container-fluid mt-4" style={{ maxWidth: 1100 }}>
      <div className="d-flex align-items-center gap-2 mb-1">
        <button className="btn btn-link p-0 text-muted" onClick={() => navigate('/trade-shows')}>← Trade Shows</button>
      </div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="mb-0">{show.name}</h4>
          <div className="text-muted small">
            {show.location}
            {show.boothNumber && <> · Booth {show.boothNumber}</>}
            {' · '}{show.startDate} → {show.endDate}
          </div>
        </div>
        <span className="badge bg-secondary text-capitalize">{show.status}</span>
      </div>

      <ul className="nav nav-tabs mb-4">
        {TABS.map(([key, label]) => (
          <li className="nav-item" key={key}>
            <button className={`nav-link ${activeTab === key ? 'active' : ''}`} onClick={() => setActiveTab(key)}>{label}</button>
          </li>
        ))}
      </ul>

      {activeTab === 'overview' && <OverviewTab pnl={pnl} />}
      {activeTab === 'leads' && <LeadsTab showId={show.id} onChanged={refreshPnl} />}
      {activeTab === 'expenses' && <ExpensesTab showId={show.id} onChanged={refreshPnl} />}
      {activeTab === 'followup' && <FollowUpTab show={show} onShowUpdated={setShow} />}
      {activeTab === 'qr' && <QrTab show={show} leads={leads} />}
    </div>
  );
}
